/**
 * WitnessTupleLabPanel — the interactive heart.
 *
 * Pick a seam regime; toggle which witnesses you currently hold; the
 * panel computes (via algorithms/compliance.ts) the missing witnesses
 * and shows the crossing as either structurally compliant
 * (constructable) or structurally inexpressible (unconstructable),
 * naming exactly which required witness is absent.
 *
 * Honest framing: holding all required witnesses makes the crossing
 * constructable; absent a required witness, the crossing is structurally
 * inexpressible — not "rejected by a guard".
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */
import React, { useMemo, useState } from "react";
import { PanelSurface, panelTokens } from "@csk/demo-ui";
import regimesData from "../data/regimes.json" with { type: "json" };
import witnessesData from "../data/witnesses.json" with { type: "json" };
import { evaluateCrossing } from "../algorithms/compliance.js";
type Regime = (typeof regimesData.regimes)[number];
const ALL_WITNESS_IDS: readonly string[] = witnessesData.witnesses.map(
  (w) => w.id,
);
const REGIME_TOKENS: Record<string, string> = {
  regimeTool: panelTokens.regimeTool,
  regimeSubAgent: panelTokens.regimeSubAgent,
  regimeExternal: panelTokens.regimeExternal,
  regimePersistent: panelTokens.regimePersistent,
};
function regimeById(id: string): Regime {
  return regimesData.regimes.find((r) => r.id === id) ?? regimesData.regimes[0];
}
export function WitnessTupleLabPanel(): React.JSX.Element {
  const [regimeId, setRegimeId] = useState<string>(regimesData.regimes[0].id);
  const [held, setHeld] = useState<ReadonlySet<string>>(() => new Set());
  const [conditions, setConditions] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const regime = useMemo(() => regimeById(regimeId), [regimeId]);
  const color = REGIME_TOKENS[regime.token] ?? panelTokens.accent;
  // Required tuple = base required witnesses plus any conditional witness
  // whose triggering condition the user has switched on.
  const required = useMemo<readonly string[]>(() => {
    const base = [...regime.required];
    for (const c of regime.conditional) {
      if (conditions.has(c.when)) base.push(c.witness);
    }
    return base;
  }, [regime, conditions]);
  // The eight Bridge Witness Suite types are always shown. A regime can also
  // require an out-of-suite witness — e.g. Release (RES.RELEASE) when a
  // persistent crossing deletes — which is not one of the eight POS-A
  // witnesses; surface a toggle for it only while its condition is active so
  // the required tuple is never silently unsatisfiable.
  const toggleWitnessIds = useMemo<readonly string[]>(() => {
    const ids = [...ALL_WITNESS_IDS];
    for (const witness of required) {
      if (!ids.includes(witness)) ids.push(witness);
    }
    return ids;
  }, [required]);
  const outOfSuite = toggleWitnessIds.filter(
    (w) => !ALL_WITNESS_IDS.includes(w),
  );
  const verdict = useMemo(
    () => evaluateCrossing(regime.id, required, [...held]),
    [regime.id, required, held],
  );
  const toggleHeld = (witness: string, next: boolean): void => {
    setHeld((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(witness);
      else updated.delete(witness);
      return updated;
    });
  };
  const toggleCondition = (when: string, next: boolean): void => {
    setConditions((prev) => {
      const updated = new Set(prev);
      if (next) updated.add(when);
      else updated.delete(when);
      return updated;
    });
  };
  const tone: "safe" | "unsafe" = verdict.constructable ? "safe" : "unsafe";
  const verdictTitle = verdict.constructable
    ? `${regime.name} crossing is CONSTRUCTABLE — structurally compliant`
    : `${regime.name} crossing is UNCONSTRUCTABLE — structurally inexpressible`;
  const verdictBody = verdict.constructable
    ? `Every required witness is held: ${required.join(", ")}. The crossing term can be formed and type-checks (ABK-4 rwt-sufficient).`
    : `Missing required witness${verdict.missing.length === 1 ? "" : "es"}: ${verdict.missing.join(", ")}. The non-compliant crossing cannot be expressed — the type system cannot form the term without ${verdict.missing.length === 1 ? "this witness" : "these witnesses"}.`;
  return (
    <PanelSurface.PanelShell
      title="Witness Tuple Lab"
      subtitle="Pick a regime · hold witnesses · watch the crossing become constructable or structurally inexpressible"
      accentColor={panelTokens.accentYellow}
    >
      <PanelSurface.Card>
        <p className="m-0 text-[14px] leading-[1.7] text-[var(--abk-text)]">
          Choose a seam regime, then toggle the witnesses you currently hold.
          The verdict tells you whether the required witness tuple is satisfied.
          A held witness makes its obligation dischargeable; an absent required
          witness makes the whole crossing{" "}
          <strong className="text-[var(--abk-bright)]">unconstructable</strong>.
        </p>
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Seam regime</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.ChipRow
          ariaLabel="Seam regime"
          activeId={regimeId}
          onChange={(id) => {
            setRegimeId(id);
            setConditions(new Set());
          }}
          items={regimesData.regimes.map((r) => ({
            id: r.id,
            label: `${r.id} · ${r.name}`,
            color: REGIME_TOKENS[r.token] ?? panelTokens.accent,
          }))}
        />
        <p className="mx-1 mt-3 mb-0 text-[12.5px] leading-[1.6] text-[var(--abk-muted)]">
          {regime.summary}
        </p>
        {regime.conditional.length > 0 && (
          <div className="mt-3 flex flex-col gap-1.5">
            {regime.conditional.map((c) => (
              <PanelSurface.Toggle
                key={c.when}
                label={`Crossing is "${c.when}" — adds ${c.witness} to the required tuple`}
                checked={conditions.has(c.when)}
                onChange={(next) => {
                  toggleCondition(c.when, next);
                }}
                color={color}
              />
            ))}
          </div>
        )}
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Verdict</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.VerdictBanner tone={tone} title={verdictTitle} body={verdictBody} />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Witnesses held</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <div className="mb-3 flex flex-wrap gap-2">
          <PanelSurface.ControlButton
            color={panelTokens.accentGreen}
            onClick={() => {
              setHeld(new Set(required));
            }}
          >
            Hold the required tuple
          </PanelSurface.ControlButton>
          <PanelSurface.ControlButton
            color={panelTokens.accentRed}
            onClick={() => {
              setHeld(new Set());
            }}
          >
            Drop every witness
          </PanelSurface.ControlButton>
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(280px,1fr))] gap-1.5">
          {toggleWitnessIds.map((witness) => {
            const isRequired = required.includes(witness);
            const isOutOfSuite = !ALL_WITNESS_IDS.includes(witness);
            const label = isOutOfSuite
              ? `${witness} (required · outside the 8-witness suite)`
              : isRequired
                ? `${witness} (required)`
                : witness;
            return (
              <PanelSurface.Toggle
                key={witness}
                label={label}
                checked={held.has(witness)}
                onChange={(next) => {
                  toggleHeld(witness, next);
                }}
                color={isRequired ? panelTokens.witnessHeld : panelTokens.muted}
              />
            );
          })}
        </div>
        {outOfSuite.length > 0 && (
          <p className="mx-1 mt-2.5 mb-0 text-[12px] leading-[1.6] text-[var(--abk-muted)]">
            {outOfSuite.join(", ")} is RES.RELEASE — a bridge witness outside
            the eight POS-A types (RD-27 §3), required only when the crossing
            deletes. It appears as a toggle here so the required tuple stays
            satisfiable.
          </p>
        )}
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>Required witness status</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.StatusList
          ariaLabel="Required witness discharge status"
          items={required.map((witness) => {
            const isPresent = verdict.present.includes(witness);
            return {
              id: witness,
              title: witness,
              status: isPresent ? "held — dischargeable" : "absent",
              tone: isPresent ? "complete" : "blocked",
              description: isPresent
                ? "The obligation this witness discharges is satisfiable."
                : "Without this witness the crossing is structurally inexpressible.",
            };
          })}
        />
      </PanelSurface.Card>

      <PanelSurface.SectionHeading>What the type system sees</PanelSurface.SectionHeading>
      <PanelSurface.Card>
        <PanelSurface.Mono>
          {verdict.constructable
            ? `-- All required witnesses in scope; the crossing term type-checks.
cross : SeamCrossing ${regime.id}
cross = mkCrossing ${required.map((w) => w.toLowerCase()).join(" ")}
  -- rwt-sufficient ${regime.id} : SeamCompliant ${regime.id}`
            : `-- Missing: ${verdict.missing.join(", ")}.
-- The non-compliant crossing cannot be written; there is no term to reject.
cross : SeamCrossing ${regime.id}
cross = mkCrossing ${verdict.present
                .map((w) => w.toLowerCase())
                .join(" ")} ${verdict.missing
                .map((w) => `⟨${w.toLowerCase()} : absent⟩`)
                .join(" ")}
  -- type error: required witness not in scope`}
        </PanelSurface.Mono>
      </PanelSurface.Card>
    </PanelSurface.PanelShell>
  );
}
